import { coversModel } from '@/core/coversModel';
import { InsuranceCovers } from '@/enums/filters';

export const PackageService = () => {
    const transformPackage = (packagePayload, duration, startingDate, selectedFeatures, autonomousProducts, proxyFeatureHandler) => {

        const { multiproduct, quotedProducts } = packagePayload;

        const transformedPackage = {};
        transformedPackage.company = {};

        // Find main product GUID and indexes
        let mainProductGUID = null;
        let mainProductIndexInQuotedProducts = null;
        let mainProductIndexInMultiProduct = null;

        // Find the main product GUID and index in multiproduct
        multiproduct.products.forEach((product, index) => {
          if (product.type.name === 'group_covers') {
            mainProductGUID = product.guid;
            mainProductIndexInMultiProduct = index;
          }
        });
        // Find the main product in quoted products
        quotedProducts.forEach((quotedProduct, index) => {
          if (quotedProduct.guid === mainProductGUID) {
            mainProductIndexInQuotedProducts = index;
          }
        });

        // Company name of the main product
        transformedPackage.company.name = multiproduct.products[mainProductIndexInMultiProduct].vendor.name;
        transformedPackage.crossSell = getCrossSellDiscount(
          quotedProducts,
          mainProductIndexInQuotedProducts
        );
 
        // Check for erros
        const errorMessage = quotedProducts[mainProductIndexInQuotedProducts]?.errorMessage;
        if (errorMessage !== '') {
          transformedPackage.hasError = true;
          transformedPackage.errorMessage = errorMessage;

          // Instantly return the transformed package
          return transformedPackage;
        } else {
          transformedPackage.hasError = false;
        }

        // Company id
        transformedPackage.company.id = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.siteId;

        // Package id
        transformedPackage.packageId = multiproduct.products[mainProductIndexInMultiProduct].attributes.siteId;

        // ID
        transformedPackage.id = multiproduct._id;

        // COMPANY SECTION
        // =======================
        // Company logo: TBD
        transformedPackage.company.logo = `${import.meta.env.VITE_METADATA_SERVER}${multiproduct.products[mainProductIndexInMultiProduct].vendor.logo}`;
        // transformedPackage.company.logo = `https://www.insurancemarket.gr/images/cmp_logos/${multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.siteId}.png`;

        // Company rating:
        transformedPackage.company.rating = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.averageRating;

        // Company rating number of votes:
        transformedPackage.company.ratingVotes = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.ratingsNumber;

        // Company description:
        transformedPackage.company.description = multiproduct.products[mainProductIndexInMultiProduct].vendor.description;

        // Company subratings:
        // - Company serviceRating
        transformedPackage.company.serviceRating = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.serviceRating;
        // - Company compensationRating
        transformedPackage.company.compensationRating = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.compensationRating;
        // - Company costEfficiencyRating
        transformedPackage.company.costEfficiencyRating = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.costEfficiencyRating;

        // Company solvency
        transformedPackage.company.solvency = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.solvency;

        // Company link read more
        transformedPackage.company.linkReadMore = multiproduct.products[mainProductIndexInMultiProduct].vendor.attributes.companyLink;

        // PRODUCT SECTION
        // =======================
        // Price TBD - Check after finishing the covers
        // maybe add a lib for math operations
        let price = 0;
        quotedProducts.forEach(quotedProduct => {
          price += parseFloat(quotedProduct.combinations[0].price);
        });
        transformedPackage.price = price;
        
        transformedPackage.monthlyPrice = price/duration;

        // Start Date
        // startDateWasEmpty: true when the insurer returned no date (empty string).
        // startDateFromProduct: true only when the insurer enforces a date that
        //   DIFFERS from the requested filter date. Some insurers echo the filter
        //   date back as a non-empty startDate; that is not a real constraint.
        let startDate = quotedProducts[mainProductIndexInQuotedProducts].startDate;
        if (startDate === '') {
          transformedPackage.startDate = startingDate;
          transformedPackage.startDateFromProduct = false;
          transformedPackage.startDateWasEmpty = true;
        } else {
          const productDateStr = new Date(startDate).toDateString();
          const filterDateStr = startingDate ? new Date(startingDate).toDateString() : null;
          const isEchoing = filterDateStr && productDateStr === filterDateStr;
          transformedPackage.startDate = isEchoing ? startingDate : productDateStr;
          transformedPackage.startDateFromProduct = !isEchoing;
          transformedPackage.startDateWasEmpty = false;
        }

        // Easy Buy
        transformedPackage.easyBuy = multiproduct.attributes.easyBuy;
        transformedPackage.easyBuyError = multiproduct.attributes.easyBuyError;
        transformedPackage.easyBuyErrorId = multiproduct.attributes.easyBuyErrorId;

        transformedPackage.groupBanners = [];
        // Has Group Banners
        if (multiproduct?.banners?.groupBanners?.length > 0) {
          multiproduct?.banners?.groupBanners.forEach(groupBanner => {
            transformedPackage.groupBanners.push({
              headerText: groupBanner?.data?.attributes?.headerText,
              bodyText: groupBanner?.data?.attributes?.bodyText,
              image: `${import.meta.env.VITE_METADATA_SERVER}${groupBanner?.data?.attributes?.image?.data?.attributes?.url}`
            });
          });
        }

        // If has offer,
        const mainBanners = multiproduct?.banners?.mainBanners;
        const neededBanners = Array.isArray(mainBanners) ? mainBanners : (mainBanners ? [mainBanners] : []);

        transformedPackage.mainBanners = [];
        transformedPackage.installments = quotedProducts[mainProductIndexInQuotedProducts].combinations[0].installments ?? [];
        if
            (neededBanners.length > 0)
         {
          transformedPackage.hasOffer = true;

           const sortedMainBanners = neededBanners.slice().sort(
               (a, b) => Number(b?.data?.id || 0) - Number(a?.data?.id || 0) // highest id first
           );

           sortedMainBanners.forEach(mainBanner => {
              try {
                transformedPackage.mainBanners.push({
                  // Offer text
                  offerText: mainBanner.data.attributes.headerText,
                  // Product Offer Description: TBD (rich text)
                  offerBodyText: mainBanner.data.attributes.bodyText,
                  // Offer terms
                  offerTermsURL: mainBanner.data.attributes.detailsUrl,
                  offerImage: `${import.meta.env.VITE_METADATA_SERVER}${mainBanner.data.attributes.image?.data?.attributes?.url}`,
                  promoImage: `${import.meta.env.VITE_METADATA_SERVER}${mainBanner.data.attributes.promoImage?.data?.attributes?.url}`,
                  promoTitle: mainBanner.data.attributes.promoTitle,
                  promoDescription: mainBanner.data.attributes.promoDescription,
                  promoModalTitle: mainBanner.data.attributes.promoModalTitle,
                  promoModalDescription: mainBanner.data.attributes.promoModalDescription,
                  type: mainBanner.data.attributes.type,
                });
              } catch (e) {
                throw Error('Couln\'t retrieve banners')
              }
            });
        }

        // EXTRA INFORMATION FOR THE OFF CANVAS TBD
        // =======================

        // Grouped Covers: TBD
        // - For each cover
        // -- Title: TBD
        // -- Capital: TBD
        // -- Description: TBD (rich text)
        // -- T&C: TBD

        // Documents: TBD
        // - For each document
        // -- Title: TBD
        // -- Link: TBD

        // Product Pros and Cons: TBD

        // Buy link: TBD


        // COVERS
        // ====================================
        // STEP 1. Make a flat array of all the features and add the parent GUID to each feature
        let flatFeaturesArray = flattenFeatures(multiproduct.products, mainProductIndexInMultiProduct, proxyFeatureHandler);


        // STEP 2. Add metadata from the config array to each feature (parentGUID, is hidden from view)
        flatFeaturesArray = addMetaDataFromConfigArray(flatFeaturesArray);


        // STEP 3. Seperate included and available to add covers
        const { includedCovers, availableToAddCovers } = seperateIncludedAndAvailableToAddCovers(flatFeaturesArray, quotedProducts, autonomousProducts, selectedFeatures);

        // STEP 4. Make them into groups according to their GUID and the covers model
        const groupedFeatures = groupFeatures(includedCovers);

        // STEP 5. Reorder the groups according to the covers model (reordering is happening in place)
        reorderedGroups(groupedFeatures);


        // Find if the package has Whole Car Theft
        includedCovers.forEach((cover) => {
          if (cover.type.guid === InsuranceCovers.Theft) {
            transformedPackage.hasTotalTheft = true;
            transformedPackage.totalTheftDeductible = 0

            // Parse only deductible number in order to be able to use it when apply total theft filter
            const regex = /^(\d+(?:,\d+)?(?:\.\d+)?)(?:\s?€?)?$/;
            let deductibleNumberMatch = regex.exec(cover.attributes?.deductibles[0]?.displayValue)
            if (deductibleNumberMatch && deductibleNumberMatch.length > 1) {
              transformedPackage.totalTheftDeductible = deductibleNumberMatch[1];
            }
          }

          if (cover.type.guid === InsuranceCovers.OwnDamages) {
            transformedPackage.ownDamagesDeductible = cover.attributes?.deductibles[0]?.displayValue || 0;
          }
        });



        // Add them to the transformed package
        transformedPackage.covers = groupedFeatures;
        transformedPackage.availableToAddCovers = availableToAddCovers;
        transformedPackage.includedCovers = includedCovers;
        transformedPackage.includedCoversFlat = includedCovers.map((cover) => cover.type.guid);



        //  Number of Covers
        let numberOfCovers = includedCovers.length;
        transformedPackage.numberOfCovers = numberOfCovers;

        // Save the whole payload for debugging
        transformedPackage.payload = packagePayload;


        return transformedPackage;
    }

    const getCrossSellDiscount = (quotedProducts, mainProductIndexInQuotedProducts) => {
      const mainProductCombinations = quotedProducts?.[mainProductIndexInQuotedProducts]?.combinations ?? [];

      for (const combination of mainProductCombinations) {
        const discount = combination?.discount;
        if (discount?.key !== 'CrossSell') {
          continue;
        }

        const parsedDiscount = Number(discount?.value);
        if (Number.isFinite(parsedDiscount) && parsedDiscount === 10) {
          return parsedDiscount;
        }
      }

      return 0;
    };


    const groupFeatures = (featuresArray) => {
        const groupedFeatures = [];

        // This array has the top level GUIDs
        const coversModelArray = Object.keys(coversModel);

        // First create the top level features
        featuresArray.forEach(feature => {
          const topLevelFeature = coversModelArray.find((coverGUID) => feature.type.guid === coverGUID);

          if (topLevelFeature) {
            // The feature is a top level feature.
            const tmpObject = {
              guid: topLevelFeature,
              data: feature,
              subfeatures: [],
            };

            // Find the subfeatures
            const subfeatures = Object.keys(coversModel[topLevelFeature]);

            if (subfeatures.length) {
              // Loop again through the features array
              featuresArray.forEach(feature => {
                if (subfeatures.includes(feature.type.guid)) {
                  // The feature is a subfeature of the top level feature. Add it.
                  tmpObject.subfeatures.push(feature);
                }
              });
            }

            // Add the tmpObject to the groupedFeatures array
            groupedFeatures.push(tmpObject);

          }
        });

        return groupedFeatures;
    }

    // Helper functions for covers transformation
    const flattenFeatures = (products, mainProductIndexInMultiProduct, proxyFeatureHandler) => {
        const flatFeaturesArray = [];
        products.forEach((product, index) => {
          product.features.forEach(feature => {
            // Add a flag if the cover is in the primary product
            feature.frontEndProperties = {};
            if (index === mainProductIndexInMultiProduct) {
              feature.frontEndProperties.isInPrimaryProduct = true;
            } else {
              feature.frontEndProperties.isInPrimaryProduct = false;
              feature.frontEndProperties.vendorName = product.vendor.name;
            }
            flatFeaturesArray.push(new Proxy(feature, proxyFeatureHandler));
          });
        });

        return flatFeaturesArray;
    }

    const addMetaDataFromConfigArray = (featuresArray) => {
        // Add the parent GUID to each feature
        featuresArray.forEach((feature) => {
          // Find the GUID in the model
          const parentGUID = Object.keys(coversModel).find((coverGUID) => feature.type.guid === coverGUID);

          // If we found the GUID, it has not parent. set it to null
          if (parentGUID) {
            feature.attributes.parentGUID = null;
            feature.attributes.hideFromView = false;
          }
          else {
            // If we didn't find the GUID, it has a parent. We need to loop through the model to find it
            Object.keys(coversModel).forEach((coverGUID) => {
              const subfeatures = Object.keys(coversModel[coverGUID]);

              if (subfeatures.includes(feature.type.guid)) {
                feature.attributes.parentGUID = coverGUID;

                // Check if it has hideFromView true
                if (coversModel[coverGUID][feature.type.guid].hideFromView) {
                  feature.attributes.hideFromView = true;
                } else {
                  feature.attributes.hideFromView = false;
                }
              }
            });
          }
        });

        return featuresArray;
    }

    const seperateIncludedAndAvailableToAddCovers = (featuresArray, quotedProducts, autonomousProducts, selectedF) => {
        let includedCovers = [];
        let availableToAddCovers = [];

        const quotedAutonomousPrdFeatureGuids = [];

        // Fill the array with all features that are inside autonomous products which
        // their prices are not zero

        if (autonomousProducts) {
          Object.values(autonomousProducts).forEach(autoProd => {
            autoProd.featureGuid?.forEach((featureGuid) => {
              if (!quotedAutonomousPrdFeatureGuids.includes(featureGuid)) {
                quotedAutonomousPrdFeatureGuids.push(featureGuid)
              }
            })
          })
        }

        featuresArray.forEach((feature) => {
          const isLinkedFeature = quotedProducts.some(product =>
              product.combinations?.some(combination =>
                  combination.features?.some(
                      featureInCombination => featureInCombination.featureTypeGuid === feature.type.guid
                  )
              ))

          // Exclude all features which are inside autonomous products but its autonomous product have zero or no price
          if (!feature.frontEndProperties.isInPrimaryProduct && !quotedAutonomousPrdFeatureGuids.includes(feature.guid)) {
            return;
          }

          const featureGUID = feature.type.guid;
          const { isMandatory, parentGUID, hideFromView } = feature.attributes;
          const selectedFeatures = selectedF || {};
          // A feature is included if it's mandatory, selected by the user, or its parent is selected by the user
          if (isLinkedFeature || isMandatory || selectedFeatures[featureGUID] || selectedFeatures[parentGUID]) {
            // If it's not hidden from view, add it to the included covers
            if (!hideFromView) {
              includedCovers = includedCovers || [];
              includedCovers.push(feature);
            }
          } else {
            availableToAddCovers = availableToAddCovers || [];
            availableToAddCovers.push(feature);
          }

        });

        return { includedCovers, availableToAddCovers };
    }

    const reorderedGroups = (covers) => {
        // Create an array from the keys of 'coversModel'
        const order = Object.keys(coversModel);

        // Sort 'covers' based on 'order'
        covers.sort((a, b) => order.indexOf(a.guid) - order.indexOf(b.guid));

        // For each object in 'covers', sort 'subfeatures' based on the corresponding keys in 'coversModel'
        covers.forEach(obj => {
          if (coversModel[obj.guid]) {
            const subOrder = Object.keys(coversModel[obj.guid]);
            obj.subfeatures.sort((a, b) => subOrder.indexOf(a.type.guid) - subOrder.indexOf(b.type.guid));
          }
        });
    };


    return {
        transformPackage,
    }
}
